import React, { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { formatAwardPeriod, monthPeriod, parsePeriodDate, periodDateValue, validPeriod } from './awardPeriod';

export default function AwardPeriodPicker({ value, onChange, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('month');
  const [draft, setDraft] = useState(value);
  const [choosingEnd, setChoosingEnd] = useState(false);
  const [month, setMonth] = useState(parsePeriodDate(value.start));
  const container = useRef(null), trigger = useRef(null), popover = useRef(null);
  const id = useId();
  const valid = validPeriod(draft);
  const changeOpen = next => { setOpen(next); onOpenChange(next); };

  useEffect(() => {
    if (!open) return;
    popover.current?.querySelector('button')?.focus();
    const outside = event => { if (!container.current?.contains(event.target)) { setOpen(false); onOpenChange(false); } };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open, onOpenChange]);

  const updateDate = (field, date) => {
    setChoosingEnd(false);
    setDraft(previous => ({ ...previous, [field]: date }));
    if (parsePeriodDate(date)) setMonth(parsePeriodDate(date));
  };
  const dismiss = () => { changeOpen(false); trigger.current?.focus(); };
  const updateMonth = value => {
    const next = monthPeriod(value);
    if (next) { setDraft(next); setMonth(parsePeriodDate(next.start)); }
  };

  return <div className="award-period" ref={container} onKeyDown={event => {
    if (open && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); dismiss(); }
  }}>
    <button ref={trigger} type="button" className="award-period__trigger" aria-label={`Award period: ${formatAwardPeriod(value)}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => {
      if (open) { dismiss(); return; }
      setDraft(value); setMonth(parsePeriodDate(value.start));
      setChoosingEnd(false);
      const fullMonth = monthPeriod(value.start.slice(0, 7));
      setMode(fullMonth?.start === value.start && fullMonth?.end === value.end ? 'month' : 'range');
      changeOpen(true);
    }}><CalendarDays size={18} aria-hidden="true"/><span><span className="award-period__label">Award period</span>{formatAwardPeriod(value)}</span><ChevronDown size={18} aria-hidden="true"/></button>
    {open && <div ref={popover} id={id} role="dialog" aria-label="Choose award period" className="award-period__popover">
      <form onSubmit={event => { event.preventDefault(); if (valid) { onChange(draft); dismiss(); } }}>
        <div className="award-period__modes" role="group" aria-label="Period selection mode">
          <button type="button" aria-pressed={mode === 'month'} onClick={() => { setMode('month'); setDraft(monthPeriod(periodDateValue(month).slice(0, 7))); }}>Month</button>
          <button type="button" aria-pressed={mode === 'range'} onClick={() => { setMode('range'); setChoosingEnd(false); }}>Custom range</button>
        </div>
        {mode === 'month' ? <label className="award-period__month">Award month<input type="month" required min="1900-01" max="9999-12" value={draft.start.slice(0, 7)} onInput={event => updateMonth(event.currentTarget.value)} onChange={event => updateMonth(event.target.value)}/></label> : <div className="award-period__dates">
          <label>Start date<input type="date" required min="1900-01-01" max="9999-12-31" value={draft.start} onInput={event => updateDate('start', event.currentTarget.value)} onChange={event => updateDate('start', event.target.value)}/></label>
          <label>End date<input type="date" required min="1900-01-01" max="9999-12-31" value={draft.end} onInput={event => updateDate('end', event.currentTarget.value)} onChange={event => updateDate('end', event.target.value)}/></label>
        </div>}
        <DayPicker mode="range" month={month} onMonthChange={next => { setMonth(next); if (mode === 'month') setDraft(monthPeriod(periodDateValue(next).slice(0, 7))); }}
          fromYear={1900} toYear={9999} showOutsideDays={false}
          selected={{ from: parsePeriodDate(draft.start), to: parsePeriodDate(draft.end) }}
          onSelect={(_, date) => {
            if (mode === 'month') { setDraft(monthPeriod(periodDateValue(date).slice(0, 7))); return; }
            const day = periodDateValue(date);
            if (!choosingEnd || !parsePeriodDate(draft.start)) { setDraft({ start: day, end: '' }); setChoosingEnd(true); }
            else { setDraft(day < draft.start ? { start: day, end: draft.start } : { start: draft.start, end: day }); setChoosingEnd(false); }
          }}
          className="award-period__calendar"
          classNames={{ months:'period-calendar__months', month:'period-calendar__month', caption:'period-calendar__caption', caption_label:'period-calendar__caption-label', nav:'period-calendar__nav', nav_button:'period-calendar__nav-button', nav_button_previous:'period-calendar__previous', nav_button_next:'period-calendar__next', table:'period-calendar__table', head_cell:'period-calendar__weekday', cell:'period-calendar__cell', day:'period-calendar__day', day_selected:'period-calendar__selected', day_range_start:'period-calendar__start', day_range_end:'period-calendar__end', day_range_middle:'period-calendar__middle', day_today:'period-calendar__today', day_hidden:'period-calendar__hidden' }}
          components={{ IconLeft: () => <ChevronLeft size={18} aria-hidden="true"/>, IconRight: () => <ChevronRight size={18} aria-hidden="true"/> }}/>
        {!valid && <p className="award-period__error" role="alert">Choose a start date and an end date on or after it.</p>}
        <button type="submit" className="award-period__apply" disabled={!valid}>Apply period</button>
      </form>
    </div>}
  </div>;
}
